import { redirect } from 'next/navigation';

/**
 * Vanity URL handler
 * This function will handle the vanity URL redirect
 *
 * More info about Vanity URL: https://dotcms.com/docs/latest/vanity-urls
 * NextJS Navigation:
 *  - permanentRedirect: https://nextjs.org/docs/app/building-your-application/routing/redirecting#permanentredirect-function
 *  - redirect: https://nextjs.org/docs/app/building-your-application/routing/redirecting#redirect-function
 *
 * @param {*} vanityUrl
 * @param {string} currentPath - Optional current path to compare against forwardTo
 */
export function handleVanityUrlRedirect({ forwardTo, action }, currentPath) {
    // Normalize paths for comparison (add leading slash if missing, remove trailing slashes)
    const normalizeUrl = (url) => {
        if (!url) return '';
        // Add leading slash if missing
        let normalized = url.startsWith('/') ? url : '/' + url;
        // Remove trailing slashes
        return normalized.replace(/\/+$/, '');
    };
    const normalizedForwardTo = normalizeUrl(forwardTo);
    const normalizedCurrentPath = normalizeUrl(currentPath);
    // Don't redirect if forwardTo is the same as the current path
    if (
        normalizedForwardTo &&
        normalizedCurrentPath &&
        normalizedForwardTo === normalizedCurrentPath
    ) {
        return;
    }

    if (action === 302) {
        redirect(forwardTo);
    } else if (action === 301) {
        redirect(forwardTo);

        // permanentRedirect(forwardTo, 'replace');
    }
}

export function featureCardContent(body) {
    let hasCopyParagraph = false;
    let hasListItems = false;
    body.content.forEach((element) => {
        if (element.type === 'paragraph') hasCopyParagraph = element;
        if (element.type === 'bulletList') hasListItems = element;
    });

    return { hasCopyParagraph, hasListItems };
}

export function contentCategory(contentTypeCategory) {
    return Object.values(contentTypeCategory[0])[0];
}

export const generateInnerHTML = (propHeading) =>
    propHeading?.content[0]?.content &&
    Object.values(propHeading?.content[0]?.content).map((element, i) => {
        const isBold =
            element.marks && element.marks.some((elementMark) => elementMark.type === 'bold');
        if (isBold) return <strong key={element + i}>{element.text}</strong>;

        if (!isBold) return <span key={element + i}>{element.text}</span>;
    });

export const screenSizes = {
    sm: '380px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px'
};

async function safeJson(res) {
    const text = await res.text();
    if (!text) return null;
    try {
        return JSON.parse(text);
    } catch (e) {
        console.error('Failed to parse JSON:', e, text);
        return null;
    }
}

/**
 * Fetches field values from a specified field inside a ContentType.
 *
 * @param {string} collection - The name of the collection to fetch field values from.
 * @param {string} field - The specific field to retrieve values for.
 * @returns {Promise<Object|null>} - A promise that resolves to the field values or null if an error occurs.
 */
export const getFieldValuesFromContentType = async (collection, field) => {
    try {
        const URL = `${process.env.NEXT_PUBLIC_DOTCMS_HOST}/api/v2/contenttype/${collection}/fields/var/${field}`;
        const res = await fetch(URL);
        const data = await safeJson(res);
        return data?.entity;
    } catch (error) {
        console.error('Error fetching data:', error);
        return null;
    }
};

/**
 * Extracts and trims the value of each multi-select field value.
 *
 * @param {string} values - The multi-select field values as a string, with each value separated by a newline.
 * @returns {string[]} An array of trimmed strings, each representing the value of the split line.
 */
export function getValuesFromMultiSelectField(values) {
    const lines = values.split(/\r?\n/);

    const words = lines.map((line) => {
        const parts = line.split('|');
        return parts[1]?.trim();
    });

    return words;
}

/**
 * Determines if a URL is relative or absolute.
 *
 * @param {*} url
 * @return {*}
 */
export function isRelativeURL(url) {
    try {
        new URL(url); // Attempt to create a URL object
        return false; // URL has an origin if this succeeds
    } catch (error) {
        return true; // URL is missing origin
    }
}

/**
 * Determines if a URL is internal (relative or from dotCMS domains).
 * Internal domains include: www.dotcms.com, dev.dotcms.com
 *
 * @param {string} url - The URL to check
 * @returns {boolean} - True if the URL is internal, false otherwise
 */
export function isInternalURL(url) {
    if (!url) {
        return false;
    }
    if (isRelativeURL(url)) {
        return true;
    }

    try {
        const urlObj = new URL(url);
        const hostname = urlObj.hostname.toLowerCase();

        return (
            hostname === 'www.dotcms.com' ||
            hostname === 'dev.dotcms.com' ||
            hostname === 'www.dev.dotcms.com' ||
            hostname === 'dotcms.com'
        );
    } catch (error) {
        return true;
    }
}

/**
 * Checks if the block editor content is empty.
 *
 * @param {Object} editorValue - The value of the editor to check.
 * @param {Object} [options={}] - Optional settings.
 * @param {boolean} [options.ignoreWhitespace=false] - Whether to ignore whitespace when checking if the editor is empty.
 * @returns {boolean} - Returns true if the editor is empty, otherwise false.
 *
 * @example
 * const editorValue = {
 *   content: [
 *     { type: 'paragraph', text: ' ' },
 *     { type: 'paragraph', text: '' }
 *   ]
 * };
 * const isEmpty = isBlockEditorEmpty(editorValue, { ignoreWhitespace: true });
 * console.log(isEmpty); // true
 */
export function isBlockEditorEmpty(editorValue, options = {}) {
    const { ignoreWhitespace = false } = options;
    let content = editorValue?.content;

    if (content === null || content === undefined) {
        content = editorValue?.json?.content;
    }

    function isNodeEmpty(node, checkChildren = true) {
        if (ignoreWhitespace) {
            if (node.type === 'hardBreak') {
                return true;
            }
            if (typeof node.text === 'string') {
                return /^\s*$/m.test(node.text);
            }
        }

        if (typeof node.text === 'string') {
            return !node.text;
        }

        if (!node.content || node.content.length === 0) {
            return true;
        }

        // Assuming atoms and leafs are never empty
        if (node.marks || (node.attrs && Object.keys(node.attrs).length > 0)) {
            return false;
        }

        if (checkChildren) {
            return node.content.every((childNode) => isNodeEmpty(childNode, true));
        }

        return false;
    }

    // Check if the value is not an object or doesn't have a 'content' property
    if (typeof content !== 'object' || !Array.isArray(content)) {
        return true;
    }

    // Check if all blocks are empty
    const val = content.every((block) => isNodeEmpty(block));
    return val;
}

/**
 * Splits an array of items into two parts at the first occurrence of a specified type.
 *
 * @param {Array} items - The array of items to split.
 * @param {string} [typeToSplit] - The type of item at which to split the array.
 * @returns {Array[]} An array containing two arrays: the first part before the split and the second part including and after the split.
 */
export function splitAfterBody(items, typeToSplit = 'bulletList') {
    if (!items) {
        return [null, null];
    }

    const index = items.findIndex((item) => item.type === typeToSplit);

    if (index === -1) {
        return [{ content: items }, { content: [] }];
    }

    const firstPart = items.slice(0, index);
    const secondPart = items.slice(index);

    return [{ content: firstPart }, { content: secondPart }];
}
